import {
  apiDelete,
  apiGet,
  apiPost,
  apiPut,
  iniciarSessaoLocal,
} from '@/shared/utils/requestBuilder';

import type {
  AcceptInviteResult,
  CreateSectorPayload,
  InviteAcceptanceItem,
  InviteRequestPayload,
  MakePrivatePayload,
  RemovePersonPayload,
  SectorSummary,
  SectorToggleRequestPayload,
  ShareItemContext,
  ShareItemModule,
  ShareMemberType,
  SharePerson,
  ShareSector,
  ShareSectorAccess,
  ShareSectorMember,
  UpdatePermissionPayload,
  UpdateSectorPayload,
  UserSuggestion,
} from '../types/compartilhamentoTypes';

interface ApiSummaryMember {
  id: string;
  nome: string;
  email: string;
  avatarUrl: string | null;
  papel: string | null;
}

interface ApiSummaryItem {
  id: string;
  nome: string;
  totalMembros: number;
  totalItensAtivos: number;
  membros: ApiSummaryMember[];
}

interface ApiAvailableUser {
  id: string;
  nomeCompleto: string;
  email: string;
  fotoPerfil: string | null;
  tipoUsuario: string;
  ativo: boolean;
}

interface ApiSharePerson {
  id: string;
  shareId: string;
  nome: string;
  email: string;
  avatarUrl: string | null;
  userType: 'internal' | 'external';
  fromOtherWorkspace?: boolean;
  permission: string;
  status: string;
  addedAt: string;
}

interface ApiShareSectorAccess {
  sectorId: string;
  shareId: string;
  permission: string;
  enabled: boolean;
}

interface ApiShareContext {
  context: ShareItemContext;
  people: ApiSharePerson[];
  sectorAccess: ApiShareSectorAccess[];
  sectors: Array<{
    id: string;
    nome: string;
    membros: ApiSummaryMember[];
    totalItensAtivos: number;
  }>;
}

const mapSummaryMember = (m: ApiSummaryMember): ShareSectorMember => ({
  id: m.id,
  nome: m.nome,
  email: m.email,
  avatarUrl: m.avatarUrl,
});

const mapApiPerson = (p: ApiSharePerson): SharePerson => ({
  id: p.shareId,
  nome: p.nome,
  email: p.email,
  avatarUrl: p.avatarUrl,
  userType: p.userType,
  fromOtherWorkspace: p.fromOtherWorkspace ?? false,
  permission: p.permission as 'view' | 'edit',
  status: p.status as 'active' | 'pending',
  addedAt: p.addedAt,
});

const mapApiSectorAccess = (a: ApiShareSectorAccess): ShareSectorAccess => ({
  sectorId: a.sectorId,
  permission: a.permission as 'view' | 'edit',
  enabled: a.enabled,
});

const parseApiError = async (response: Response, fallback: string): Promise<string> => {
  const body = await response.json().catch(() => null);
  if (!body || typeof body !== 'object') return fallback;

  const record = body as Record<string, unknown>;

  if (typeof record.error === 'string' && record.error.trim()) return record.error;
  if (record.error && typeof record.error === 'object') {
    const errorObj = record.error as Record<string, unknown>;
    if (typeof errorObj.message === 'string' && errorObj.message.trim()) return errorObj.message;
  }
  if (typeof record.message === 'string' && record.message.trim()) return record.message;

  return fallback;
};

export const compartilhamentoService = {
  // ─── Sharing modal (real API) ───

  getItemContext: async (
    module: ShareItemModule,
    id: string,
    _title?: string,
  ): Promise<{
    context: ShareItemContext;
    people: SharePerson[];
    sectorAccess: ShareSectorAccess[];
    sectors: ShareSector[];
  }> => {
    const response = await apiGet(`/compartilhamento/${module}/${id}`);

    if (!response.ok) {
      const msg = await parseApiError(response, 'Erro ao carregar compartilhamento.');
      throw new Error(msg);
    }

    const result = await response.json();
    const data = result.data as ApiShareContext;

    return {
      context: data.context,
      people: data.people.map(mapApiPerson),
      sectorAccess: data.sectorAccess.map(mapApiSectorAccess),
      sectors: data.sectors.map((s) => ({
        id: s.id,
        nome: s.nome,
        membros: s.membros.map(mapSummaryMember),
        totalItensAtivos: s.totalItensAtivos,
      })),
    };
  },

  searchSuggestions: async (query: string): Promise<UserSuggestion[]> => {
    const normalized = query.trim();
    if (!normalized) return [];

    const response = await apiGet(
      `/compartilhamento/suggestions?search=${encodeURIComponent(normalized)}`,
    );
    if (!response.ok) return [];

    const result = await response.json();
    const users = (result.data ?? []) as Array<{
      id: string;
      nomeCompleto: string;
      email: string;
      fotoPerfil?: string | null;
      isInCurrentWorkspace: boolean;
      otherWorkspaceName: string | null;
    }>;

    const seen = new Set<string>();
    return users.flatMap((u) => {
      if (seen.has(u.id)) return [];
      seen.add(u.id);
      return [
        {
          id: u.id,
          nome: u.nomeCompleto,
          email: u.email,
          avatarUrl: u.fotoPerfil ?? null,
          userType: 'internal' as ShareMemberType,
          otherWorkspaceName: u.isInCurrentWorkspace ? null : u.otherWorkspaceName,
        },
      ];
    });
  },

  invitePerson: async (
    payload: InviteRequestPayload,
  ): Promise<{ people: SharePerson[]; invitedId: string }> => {
    const { module, itemId, query, permission } = payload;
    const trimmed = query.trim();

    if (!trimmed) {
      throw new Error('Digite um nome ou e-mail para convidar.');
    }

    const response = await apiPost(`/compartilhamento/${module}/${itemId}/invite`, {
      email: trimmed,
      permission,
    });

    if (!response.ok) {
      const msg = await parseApiError(response, 'Erro ao enviar convite.');
      throw new Error(msg);
    }

    const result = await response.json();
    const data = result.data as { people: ApiSharePerson[]; invitedId: string };

    return {
      people: data.people.map(mapApiPerson),
      invitedId: data.invitedId,
    };
  },

  updatePermission: async (payload: UpdatePermissionPayload): Promise<SharePerson[]> => {
    const { module, itemId, personId, permission } = payload;

    const response = await apiPut(
      `/compartilhamento/${module}/${itemId}/shares/${personId}/permission`,
      { permission },
    );

    if (!response.ok) {
      const msg = await parseApiError(response, 'Erro ao atualizar permissão.');
      throw new Error(msg);
    }

    const contextResponse = await apiGet(`/compartilhamento/${module}/${itemId}`);
    if (!contextResponse.ok) return [];
    const contextResult = await contextResponse.json();
    const ctx = contextResult.data as ApiShareContext;
    return ctx.people.map(mapApiPerson);
  },

  removePerson: async (payload: RemovePersonPayload): Promise<SharePerson[]> => {
    const { module, itemId, personId } = payload;

    const response = await apiDelete(`/compartilhamento/${module}/${itemId}/shares/${personId}`);

    if (!response.ok) {
      const msg = await parseApiError(response, 'Erro ao remover acesso.');
      throw new Error(msg);
    }

    const contextResponse = await apiGet(`/compartilhamento/${module}/${itemId}`);
    if (!contextResponse.ok) return [];
    const contextResult = await contextResponse.json();
    const ctx = contextResult.data as ApiShareContext;
    return ctx.people.map(mapApiPerson);
  },

  toggleSectorAccess: async (payload: SectorToggleRequestPayload): Promise<ShareSectorAccess[]> => {
    const { module, itemId, sectorId, enabled, permission } = payload;

    if (enabled) {
      const response = await apiPost(`/compartilhamento/${module}/${itemId}/sectors/${sectorId}`, {
        permission: permission ?? 'view',
      });
      if (!response.ok) {
        const msg = await parseApiError(response, 'Erro ao compartilhar com setor.');
        throw new Error(msg);
      }
    } else {
      const response = await apiDelete(`/compartilhamento/${module}/${itemId}/sectors/${sectorId}`);
      if (!response.ok) {
        const msg = await parseApiError(response, 'Erro ao revogar acesso do setor.');
        throw new Error(msg);
      }
    }

    const contextResponse = await apiGet(`/compartilhamento/${module}/${itemId}`);
    if (!contextResponse.ok) return [];
    const contextResult = await contextResponse.json();
    const ctx = contextResult.data as ApiShareContext;
    return ctx.sectorAccess.map(mapApiSectorAccess);
  },

  updateSectorPermission: async (
    payload: SectorToggleRequestPayload,
  ): Promise<ShareSectorAccess[]> => {
    return compartilhamentoService.toggleSectorAccess(payload);
  },

  makePrivate: async (
    payload: MakePrivatePayload,
  ): Promise<{ people: SharePerson[]; sectorAccess: ShareSectorAccess[] }> => {
    const response = await apiPost(
      `/compartilhamento/${payload.module}/${payload.itemId}/make-private`,
      {},
    );

    if (!response.ok) {
      const msg = await parseApiError(response, 'Erro ao tornar privado.');
      throw new Error(msg);
    }

    return { people: [], sectorAccess: [] };
  },

  generateInviteLink: async (module: ShareItemModule, itemId: string): Promise<string> => {
    const response = await apiPost(`/compartilhamento/${module}/${itemId}/link`, {});

    if (!response.ok) {
      const msg = await parseApiError(response, 'Erro ao gerar link.');
      throw new Error(msg);
    }

    const result = await response.json();
    return result.data.url as string;
  },

  getInvitePreview: async (token: string): Promise<InviteAcceptanceItem> => {
    const response = await apiGet(`/compartilhamento/invite/${token}`);

    if (!response.ok) {
      const msg = await parseApiError(response, 'Convite não encontrado.');
      throw new Error(msg);
    }

    const result = await response.json();
    return result.data as InviteAcceptanceItem;
  },

  acceptInvite: async (
    token: string,
    data: { fullName?: string; email?: string; cpf?: string },
  ): Promise<AcceptInviteResult> => {
    const response = await apiPost(`/compartilhamento/invite/${token}/accept`, {
      fullName: data.fullName,
      email: data.email,
      cpf: data.cpf,
    });

    if (!response.ok) {
      const msg = await parseApiError(response, 'Erro ao aceitar convite.');
      throw new Error(msg);
    }

    iniciarSessaoLocal();

    const result = await response.json();
    return result.data as AcceptInviteResult;
  },

  requestLoginCode: async (
    email: string,
    module: ShareItemModule,
    itemId: string,
  ): Promise<void> => {
    const response = await apiPost('/compartilhamento/login/request-code', {
      email,
      module,
      itemId,
    });

    if (!response.ok) {
      const msg = await parseApiError(response, 'Erro ao enviar código.');
      throw new Error(msg);
    }
  },

  verifyLoginCode: async (
    email: string,
    code: string,
    module: ShareItemModule,
    itemId: string,
  ): Promise<{
    itemModule: ShareItemModule;
    itemId: string;
    permission: 'view' | 'edit';
  }> => {
    const response = await apiPost('/compartilhamento/login/verify-code', {
      email,
      code,
      module,
      itemId,
    });

    if (!response.ok) {
      const msg = await parseApiError(response, 'Código inválido.');
      throw new Error(msg);
    }

    iniciarSessaoLocal();

    const result = await response.json();
    return {
      itemModule: result.data.itemModule as ShareItemModule,
      itemId: result.data.itemId as string,
      permission: (result.data.permission as 'view' | 'edit') ?? 'view',
    };
  },

  declineInvite: async (token: string): Promise<string> => {
    const response = await apiPost(`/compartilhamento/invite/${token}/decline`, {});

    if (!response.ok) {
      const msg = await parseApiError(response, 'Erro ao recusar convite.');
      throw new Error(msg);
    }

    return token;
  },

  saveShares: async (
    module: ShareItemModule,
    itemId: string,
    people: Array<{ email: string; userId?: string; permission: string }>,
    sectors: Array<{ sectorId: string; permission: string }>,
    itemTitle?: string,
  ): Promise<{
    context: ShareItemContext;
    people: SharePerson[];
    sectorAccess: ShareSectorAccess[];
    sectors: ShareSector[];
  }> => {
    const response = await apiPost(`/compartilhamento/${module}/${itemId}/save`, {
      itemTitle,
      people,
      sectors,
    });

    if (!response.ok) {
      const msg = await parseApiError(response, 'Erro ao salvar compartilhamento.');
      throw new Error(msg);
    }

    const result = await response.json();
    const data = result.data as ApiShareContext;

    return {
      context: data.context,
      people: data.people.map(mapApiPerson),
      sectorAccess: data.sectorAccess.map(mapApiSectorAccess),
      sectors: data.sectors.map((s) => ({
        id: s.id,
        nome: s.nome,
        membros: s.membros.map(mapSummaryMember),
        totalItensAtivos: s.totalItensAtivos,
      })),
    };
  },

  getScopedItemInfo: async (
    module: ShareItemModule,
    itemId: string,
  ): Promise<{
    itemId: string;
    itemModule: ShareItemModule;
    itemTitle: string;
    permission: 'view' | 'edit';
    invitedByName: string;
    invitedByEmail: string;
    acceptedAt: string;
  }> => {
    const response = await apiGet(`/compartilhamento/${module}/${itemId}/item-info`);

    if (!response.ok) {
      const msg = await parseApiError(response, 'Não foi possível carregar o item compartilhado.');
      throw new Error(msg);
    }

    const result = await response.json();
    return result.data;
  },

  // ─── Workspace members ───

  loadWorkspaceMembers: async (
    workspaceId: string,
  ): Promise<Array<{ id: string; nome: string; email: string; avatarUrl: string | null }>> => {
    const response = await apiGet(`/escritorios/${workspaceId}/membros?ativo=true`);
    if (!response.ok) return [];

    const result = await response.json();
    const membros = (result.data ?? []) as Array<{
      ativo: boolean;
      usuario?: {
        id: string;
        nomeCompleto: string;
        email: string;
        fotoPerfil?: string | null;
      };
    }>;

    return membros
      .filter((m) => m.ativo && m.usuario)
      .map((m) => ({
        id: m.usuario!.id,
        nome: m.usuario!.nomeCompleto,
        email: m.usuario!.email,
        avatarUrl: m.usuario!.fotoPerfil ?? null,
      }));
  },

  // ─── Sector CRUD (real API) ───

  listSectorsSummary: async (): Promise<SectorSummary[]> => {
    const response = await apiGet('/setores/summary');

    if (!response.ok) {
      const msg = await parseApiError(response, 'Erro ao carregar setores.');
      throw new Error(msg);
    }

    const result = await response.json();
    const items: ApiSummaryItem[] = result.data;

    return items.map((item) => ({
      id: item.id,
      nome: item.nome,
      totalMembros: item.totalMembros,
      totalItensAtivos: item.totalItensAtivos,
      membros: item.membros.map(mapSummaryMember),
    }));
  },

  createSector: async (payload: CreateSectorPayload): Promise<SectorSummary> => {
    const nomeTrim = payload.nome.trim();
    if (!nomeTrim) {
      throw new Error('Informe o nome do setor.');
    }

    const response = await apiPost('/setores', {
      nome: nomeTrim,
      membrosIds: payload.membrosIds,
    });

    if (!response.ok) {
      const msg = await parseApiError(response, 'Erro ao criar setor.');
      throw new Error(msg);
    }

    const result = await response.json();
    const setor = result.data;

    const membersResponse = await apiGet(`/setores/${setor.id}/usuarios`);
    let membros: ShareSectorMember[] = [];
    if (membersResponse.ok) {
      const membersResult = await membersResponse.json();
      membros = (membersResult.data as ApiAvailableUser[]).map((u) => ({
        id: u.id,
        nome: u.nomeCompleto,
        email: u.email,
        avatarUrl: u.fotoPerfil,
      }));
    }

    return {
      id: setor.id,
      nome: setor.nome,
      totalMembros: membros.length,
      totalItensAtivos: 0,
      membros,
    };
  },

  updateSector: async (payload: UpdateSectorPayload): Promise<SectorSummary> => {
    const nomeTrim = payload.nome.trim();
    if (!nomeTrim) throw new Error('Informe o nome do setor.');

    const response = await apiPut(`/setores/${payload.id}`, {
      nome: nomeTrim,
      membrosIds: payload.membrosIds,
    });

    if (!response.ok) {
      const msg = await parseApiError(response, 'Erro ao atualizar setor.');
      throw new Error(msg);
    }

    const result = await response.json();
    const setor = result.data;

    const membersResponse = await apiGet(`/setores/${setor.id}/usuarios`);
    let membros: ShareSectorMember[] = [];
    if (membersResponse.ok) {
      const membersResult = await membersResponse.json();
      membros = (membersResult.data as ApiAvailableUser[]).map((u) => ({
        id: u.id,
        nome: u.nomeCompleto,
        email: u.email,
        avatarUrl: u.fotoPerfil,
      }));
    }

    return {
      id: setor.id,
      nome: setor.nome,
      totalMembros: membros.length,
      totalItensAtivos: setor._count?.execucoes ?? 0,
      membros,
    };
  },

  deleteSector: async (id: string): Promise<string> => {
    const response = await apiDelete(`/setores/${id}`);

    if (!response.ok) {
      const msg = await parseApiError(response, 'Erro ao excluir setor.');
      throw new Error(msg);
    }

    return id;
  },
};
